import Logger from "../../clients/sentry";
import { handleError } from "../../errors";
import { ArcolExport } from "../../integrations/arcol/types";
import { importArcolData, ImportResult } from "../../integrations/arcol/arcolImport";
import { selectJsonFile } from "../../integrations/arcol/fileReader";
import { fetchRegions } from "../../api/admin";

interface Region {
  id: string;
  name: string;
}

type ProgressCallback = (step: string, detail: string) => void;

export function useImportActions() {
  const loadJsonFile = async (): Promise<ArcolExport> => {
    try {
      return await selectJsonFile();
    } catch (error) {
      const err = handleError(error, { context: "loadJsonFile" });
      Logger.error("Failed to load Arcol JSON file", err);
      throw err;
    }
  };

  const getRegions = async (): Promise<Region[]> => {
    const result = await fetchRegions();
    if (!result.success) {
      const err = handleError(result.error, { context: "getRegions" });
      Logger.error("Failed to load regions for Arcol import", err);
      return [];
    }
    return result.data || [];
  };

  const runImport = async (
    data: ArcolExport,
    regionId: string,
    onProgress?: ProgressCallback
  ): Promise<ImportResult> => {
    try {
      const result = await importArcolData(data, regionId, onProgress);

      const errors = result.log.filter((e) => e.status === "error");
      if (errors.length > 0) {
        const err = handleError(new Error("Arcol import completed with errors"), {
          context: "runImport",
          projectName: data.project_name,
          projectId: result.projectId,
          estimateId: result.estimateId,
          errorCount: errors.length,
          errors: errors.map((e) => `${e.step}: ${e.message}`),
        });
        Logger.error("Arcol import completed with errors", err);
      }

      return result;
    } catch (error) {
      const err = handleError(error, {
        context: "runImport",
        projectName: data.project_name,
        regionId,
      });
      Logger.error("Arcol import failed", err);
      throw err;
    }
  };

  return { loadJsonFile, getRegions, runImport };
}
